import { Link } from 'react-router-dom';
import { ROUTES } from '../../lib/constants';
import { HeroStats } from './HeroStats';

/**
 * Top-of-page hero. Structure mirrors `_archive/home.html` lines
 * 849–905: centered content over a background image, a pill-style
 * eyebrow, a headline with a gold-highlighted phrase, a supporting
 * paragraph, two pill CTAs, a thin divider, and the animated stats row.
 *
 * CR-027 (CEW chrome parity spec, 2026-08-17; deliberate DEC-007-style
 * divergence from the archived HTML reference, same class as DEC-077):
 * Frank asked to replace the Unsplash city-skyline stock photo with
 * Georgetown's own illustration (`/assets/hero-springboard.jpg` — a
 * leaping figure + five gold medallions). The flat 83%-navy wash that
 * hid the old photo is replaced with a vertical gradient (lighter at
 * top, where the illustration reads brightest) so the artwork stays
 * visible while text contrast holds; the supporting paragraph's
 * opacity is raised 72% -> 92% to compensate.
 */
export function Hero() {
  return (
    <section
      id="hero"
      aria-labelledby="hero-title"
      className="relative flex min-h-[480px] items-center justify-center overflow-hidden px-6 pt-14 pb-12 text-center md:px-12 md:pt-20 md:pb-[72px]"
    >
      {/* Background illustration (CR-027 -- local asset, replaces the old
          Unsplash city-skyline stock photo). `center 60%` (2026-08-18,
          contrast fix -- see gradient note below) shifts the crop lower
          than the earlier `center 40%`, moving the brightest sky band
          and the gold medallions further above the text column so the
          darker trampoline/figure region sits behind the headline and
          paragraph instead. */}
      <div
        role="presentation"
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('/assets/hero-springboard.jpg')",
          backgroundPosition: 'center 60%',
        }}
      />
      {/* Vertical gradient overlay (CR-027; re-tuned 2026-08-18,
          `e2e/hero-contrast.spec.ts` per-pixel WCAG 1.4.3 fix -- 4th
          pass). A single gradient applies at every viewport width. The
          3rd pass's .83/.79/.69 ramp only measured the paragraph + gold
          headline; a 4th, more exhaustive review pass (per-pixel,
          worst-0.1%-excluded) measured every OTHER hero text node --
          eyebrow pill, both CTAs, the white headline half, and the
          HeroStats <dt>/<dd> pairs -- and found the bottom of the
          gradient (where the .69 stop let the illustration show through
          brightest) too light for the lower-third content: HeroStats
          <dt> measured 4.20-4.38:1 and the "How It Works" CTA text
          4.26-4.43:1, both under the 4.5:1 AA floor. Fix: the gradient's
          trailing stop is darkened back up to match the leading stop --
          a symmetric .83/.79(60%)/.83 ramp, so the lower third (stats
          row) gets the same coverage as the top (eyebrow) instead of
          trailing off lightest where the most text density sits. That
          alone cleared HeroStats <dt> (still text-white/85, untouched --
          CR-022/023 already set that opacity and it now clears with
          margin); the "How It Works" CTA needed one more nudge because
          its own translucent chrome (see the button below) lightens the
          pixels UNDER its text independent of the hero gradient -- its
          text was raised /0.90 -> solid white and its chrome's own
          bg-white/10 -> bg-white/[0.04] (a darker, less lightening
          glass panel) at the same time.
          Measured result (1st-percentile / worst-0.1%-excluded, at
          390/768/1024/1440 respectively): paragraph 5.26/5.25/5.25/5.26
          (floor 4.5), gold headline 3.64/3.43/3.38/3.38 (floor 3.0),
          white headline half 7.06/7.05/6.95/6.94 (floor 3.0), eyebrow
          pill text 5.06/5.06/5.00/4.92 (floor 4.5), "Explore the Map"
          CTA 5.00/5.00/5.00/5.00 (floor 4.5, unaffected -- solid gold
          pill, no gradient dependency), "How It Works" CTA
          5.15/5.14/5.14/5.14 (floor 4.5), HeroStats <dt>
          5.13/5.19/5.32/5.33 (floor 4.5), HeroStats <dd>
          6.63/6.54/6.54/6.54 (floor 3.0) -- every case clears its floor
          with margin at all four viewports. The illustration (leaping
          figure + medallions) stays recognizable at all four widths
          (verified by screenshot) -- darker than the original design
          but still legible, the trade-off DEC-077-style divergences
          like this one accept in exchange for meeting WCAG AA. */}
      <div
        role="presentation"
        aria-hidden="true"
        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(20,65,117,.83)_0%,rgba(20,65,117,.79)_60%,rgba(20,65,117,.83)_100%)]"
      />

      {/* CR-33 (Maria 2026-07-08 evening): the hero CONTAINER was the real
          width constraint — the CR-29 change widened the description's own
          cap 680→740, but it sits inside this container which was ALSO
          740px, so nothing moved visually. Widened the container to 880 so
          the description genuinely gains ~140px (≈1-2 fewer wrapped lines);
          the title/eyebrow/CTAs are short and stay visually centered. */}
      <div className="relative z-10 mx-auto max-w-hero">
        <span className="mb-7 inline-flex items-center gap-[7px] rounded-pill border border-white/[0.22] bg-white/[0.08] px-4 py-[7px] text-xs font-medium tracking-[0.01em] text-white/85 backdrop-blur">
          <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-gold" aria-hidden="true" />
          Georgetown University Center on Education and the Workforce
        </span>

        <h1
          id="hero-title"
          className="mb-5 text-3xl font-extrabold leading-[1.12] tracking-[-0.02em] text-white md:text-5xl"
          style={{ fontSize: 'clamp(28px, 4vw, 52px)' }}
        >
          <span className="text-gold">Springboards</span> to Opportunity
        </h1>

        {/* 2026-07-02 homepage copy refresh (Georgetown / Frank ask):
            new intro paragraph shifts the framing from "gaps / close them"
            to a concrete "where providers are awarding too few credentials
            to meet employer demand" narrative. Verbatim from the delivered
            PDF spec.
            Width history (CR-11 → CR-29 → CR-33 → TD-03): the prototype's
            540px cap was sized for much shorter copy; CR-11 widened to 680,
            CR-29 to 740 — but 740 equaled the CONTAINER cap, so that change
            was visually inert (a cap inside an equal cap is theater). CR-33
            widened the container to 880. TD-03 (DEC-075: one owner per
            visual constraint) then DELETED this paragraph's own cap — the
            hero container (line 37) is the single width owner, so a future
            "widen the hero" edit has exactly one dial and cannot be inert. */}
        <p className="mx-auto mb-9 text-[17px] leading-[1.7] text-white/[0.92]">
          Across the 55 largest US metro areas, institutions are producing roughly 30% of
          the credentials needed to fill middle-skills springboard occupations &ndash; the
          occupations that offer workers above-median earnings at the start of their careers
          and strong wage growth over time, compared with other middle-skills work. This tool
          shows where providers are awarding too few credentials to meet employer demand;
          how severe the shortages are; and where providers, employers, and policymakers can
          work together to address these shortfalls.
        </p>

        <div className="mb-[52px] flex flex-wrap justify-center gap-3">
          <Link
            to={ROUTES.map}
            className="inline-flex items-center gap-2 rounded-pill bg-gold px-7 py-[13px] text-sm font-bold text-navy shadow-[0_4px_20px_rgba(249,162,28,0.35)] transition hover:brightness-110 hover:-translate-y-px"
          >
            Explore the Map
          </Link>
          <a
            href="#how-it-works"
            className="inline-flex items-center gap-2 rounded-pill border border-white/30 bg-white/[0.04] px-7 py-[13px] text-sm font-semibold text-white backdrop-blur transition hover:bg-white/[0.16] hover:border-white/55"
          >
            How It Works
          </a>
          {/* CR-024 (UIS review, 2026-07-31): the third (report) CTA was
              REMOVED per Frank — "remove the one on the top right on the top
              banner." The persistent report affordance lives in SectionNav's
              strip + the mobile drawer (CR-034: "Download Full Report" PDF);
              the hero keeps its two CTAs so the gold "Explore the Map" stays
              dominant. */}
        </div>

        <hr className="mx-auto mt-0 mb-9 w-full max-w-[700px] border-0 border-t border-white/15" />

        <HeroStats />
      </div>
    </section>
  );
}
